import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import empresasAdminService from '../services/empresasAdminService';
import type {
  AsignarUsuarioRequest,
  UpdateUsuarioEmpresaRequest,
  UsuariosFiltros
} from '../types';

// ===============================
// GESTIÓN DE USUARIOS POR EMPRESA
// ===============================

/**
 * 👥 Hook para obtener usuarios asignados a una empresa
 */
export const useUsuariosEmpresa = (empresaId: number) => {
  return useQuery(
    ['usuarios-empresa', empresaId],
    () => empresasAdminService.obtenerUsuariosEmpresa(empresaId),
    {
      enabled: !!empresaId,
      staleTime: 5 * 60 * 1000, // 5 minutos
      retry: 2,
      onError: (error: any) => {
        console.error('Error al cargar usuarios de la empresa:', error);
      }
    }
  );
};

/**
 * 🔍 Hook para buscar usuarios en el auth service
 */
export const useBuscarUsuarios = (filtros: UsuariosFiltros) => {
  return useQuery(
    ['buscar-usuarios', filtros],
    () => empresasAdminService.buscarUsuarios(filtros),
    {
      enabled: false, // Se ejecuta manualmente
      staleTime: 2 * 60 * 1000, // 2 minutos
      retry: 1,
      onError: (error: any) => {
        console.error('Error al buscar usuarios:', error);
      }
    }
  );
};

/**
 * 👥 Hook para obtener usuarios disponibles (sin empresa)
 */
export const useUsuariosDisponibles = () => {
  return useQuery(
    'usuarios-disponibles',
    () => empresasAdminService.obtenerUsuariosDisponibles(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutos
      retry: 2,
      onError: (error: any) => {
        console.error('Error al cargar usuarios disponibles:', error);
      }
    }
  );
};

// ===============================
// MUTACIONES DE USUARIOS
// ===============================

/**
 * ➕ Hook para asignar usuario a empresa
 */
export const useAsignarUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: { empresaId: number; datos: AsignarUsuarioRequest }) => {
      return empresasAdminService.asignarUsuario(params.empresaId, params.datos);
    },
    {
      onSuccess: (data, variables) => {
        // Invalidar y refetch usuarios de la empresa
        queryClient.invalidateQueries(['usuarios-empresa', variables.empresaId]);
        
        // Invalidar búsquedas de usuarios (para actualizar contadores)
        queryClient.invalidateQueries(['buscar-usuarios']);
        
        // Invalidar usuarios disponibles
        queryClient.invalidateQueries('usuarios-disponibles');
        
        // Invalidar lista de empresas (para actualizar contadores)
        queryClient.invalidateQueries(['empresas-admin']);

        toast.success(data.message || 'Usuario asignado exitosamente');
      },
      onError: (error: any) => {
        const message = error.response?.data?.detail || 'Error al asignar usuario';
        toast.error(message);
        console.error('Error al asignar usuario:', error);
      }
    }
  );
};

/**
 * ✏️ Hook para actualizar permisos de usuario en empresa
 */
export const useActualizarUsuarioEmpresa = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: { 
      empresaId: number; 
      usuarioId: number; 
      datos: UpdateUsuarioEmpresaRequest 
    }) => {
      return empresasAdminService.actualizarUsuarioEmpresa(
        params.empresaId, 
        params.usuarioId, 
        params.datos
      );
    },
    {
      onSuccess: (data, variables) => {
        // Invalidar usuarios de la empresa específica
        queryClient.invalidateQueries(['usuarios-empresa', variables.empresaId]);
        
        // Invalidar búsquedas si se cambió el estado
        if (variables.datos.es_activa !== undefined) {
          queryClient.invalidateQueries(['buscar-usuarios']);
          queryClient.invalidateQueries('usuarios-disponibles');
        }

        toast.success(data.message || 'Permisos actualizados exitosamente');
      },
      onError: (error: any) => {
        const message = error.response?.data?.detail || 'Error al actualizar permisos';
        toast.error(message);
        console.error('Error al actualizar usuario empresa:', error);
      }
    }
  );
};

/**
 * 🗑️ Hook para desasignar usuario de empresa
 */
export const useDesasignarUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: { empresaId: number; usuarioId: number }) => {
      return empresasAdminService.desasignarUsuario(params.empresaId, params.usuarioId);
    },
    {
      onSuccess: (data, variables) => {
        // Invalidar y refetch usuarios de la empresa
        queryClient.invalidateQueries(['usuarios-empresa', variables.empresaId]);
        
        // Invalidar búsquedas de usuarios
        queryClient.invalidateQueries(['buscar-usuarios']);
        
        // Invalidar usuarios disponibles
        queryClient.invalidateQueries('usuarios-disponibles');
        
        // Invalidar lista de empresas (para actualizar contadores)
        queryClient.invalidateQueries(['empresas-admin']);

        toast.success(data.message || 'Usuario desasignado exitosamente');
      },
      onError: (error: any) => {
        const message = error.response?.data?.detail || 'Error al desasignar usuario';
        toast.error(message);
        console.error('Error al desasignar usuario:', error);
      }
    }
  );
};

// ===============================
// HELPERS Y UTILIDADES
// ===============================

/**
 * 📊 Hook para obtener estadísticas de usuarios por empresa
 */
export const useEstadisticasUsuarios = (empresaId: number) => {
  const { data: usuarios, isLoading } = useUsuariosEmpresa(empresaId);

  const estadisticas = useMemo(() => {
    if (!usuarios) {
      return {
        total: 0,
        activos: 0,
        inactivos: 0,
        por_rol: {
          administrador: 0,
          supervisor: 0,
          operador: 0
        },
        empresas_principales: 0
      };
    }

    const total = usuarios.length;
    const activos = usuarios.filter(u => u.es_activa).length;
    const inactivos = total - activos;
    
    const por_rol = usuarios.reduce((acc, usuario) => {
      if (usuario.es_activa) {
        acc[usuario.rol_en_empresa] = (acc[usuario.rol_en_empresa] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    const empresas_principales = usuarios.filter(u => u.es_empresa_principal && u.es_activa).length;

    return {
      total,
      activos,
      inactivos,
      por_rol: {
        administrador: por_rol.administrador || 0,
        supervisor: por_rol.supervisor || 0,
        operador: por_rol.operador || 0
      },
      empresas_principales
    };
  }, [usuarios]);

  return {
    estadisticas,
    isLoading
  };
};

/**
 * 🔧 Hook para lógica de asignación masiva
 */
export const useAsignacionMasiva = () => {
  const queryClient = useQueryClient();

  return useMutation(
    empresasAdminService.asignarMasivo,
    {
      onSuccess: (data) => {
        // Invalidar todas las queries relacionadas
        queryClient.invalidateQueries(['usuarios-empresa']);
        queryClient.invalidateQueries(['buscar-usuarios']);
        queryClient.invalidateQueries('usuarios-disponibles');
        queryClient.invalidateQueries(['empresas-admin']);

        toast.success(data.message || 'Usuarios asignados exitosamente');
      },
      onError: (error: any) => {
        const message = error.response?.data?.detail || 'Error en asignación masiva';
        toast.error(message);
        console.error('Error en asignación masiva:', error);
      }
    }
  );
};

// ===============================
// FUNCIONES DE VALIDACIÓN
// ===============================

/**
 * ✅ Validar permisos de usuario antes de asignación
 */
export const validarPermisos = (rol: string, permisos: any) => {
  const errores: string[] = [];

  // Validaciones específicas por rol
  switch (rol) {
    case 'administrador':
      // Los administradores deben tener permisos de gestión
      if (!permisos.puede_gestionar_usuarios) {
        errores.push('Los administradores deben poder gestionar usuarios');
      }
      break;
    
    case 'supervisor':
      // Los supervisores deben poder ver reportes de otros
      if (!permisos.puede_ver_reportes_otros) {
        errores.push('Los supervisores deben poder ver reportes de otros');
      }
      break;
    
    case 'operador':
      // Los operadores al menos deben poder crear reportes
      if (!permisos.puede_crear_reportes) {
        errores.push('Los operadores deben poder crear reportes');
      }
      break;
  }

  return errores;
};

/**
 * 🔧 Obtener permisos por defecto para un rol
 */
export const getPermisosDefecto = (rol: string) => {
  switch (rol) {
    case 'administrador':
      return {
        puede_crear_reportes: true,
        puede_editar_reportes_propios: true,
        puede_ver_reportes_otros: true,
        puede_editar_reportes_otros: true,
        puede_aprobar_reportes: true,
        puede_gestionar_referencias: true,
        puede_gestionar_usuarios: true
      };
    
    case 'supervisor':
      return {
        puede_crear_reportes: true,
        puede_editar_reportes_propios: true,
        puede_ver_reportes_otros: true,
        puede_editar_reportes_otros: false,
        puede_aprobar_reportes: true,
        puede_gestionar_referencias: true,
        puede_gestionar_usuarios: false
      };
    
    case 'operador':
    default:
      return {
        puede_crear_reportes: true,
        puede_editar_reportes_propios: true,
        puede_ver_reportes_otros: false,
        puede_editar_reportes_otros: false,
        puede_aprobar_reportes: false,
        puede_gestionar_referencias: false,
        puede_gestionar_usuarios: false
      };
  }
};
